import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("responsia_token"));

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Fetch user on mount
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data.user);
        setOrganization(response.data.organization);
        setSubscription(response.data.subscription);
      } catch (error) {
        console.error("Auth error:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("responsia_token", newToken);
    setToken(newToken);
    setUser(userData);
    
    // Fetch full user data
    const meResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setOrganization(meResponse.data.organization);
    setSubscription(meResponse.data.subscription);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    // New flow: registration requires email verification before login
    // Don't store token or set user - redirect to verification pending page
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("responsia_token");
    setToken(null);
    setUser(null);
    setOrganization(null);
    setSubscription(null);
  };

  const refreshUserData = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setOrganization(response.data.organization);
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error("Refresh error:", error);
    }
  };

  const isPremium = () => {
    return subscription?.plan === "premium" && (subscription?.status === "active" || subscription?.status === "trial");
  };

  const hasSubscription = () => {
    return subscription?.status === "active" || subscription?.status === "trial";
  };

  const isTrialActive = () => {
    return subscription?.status === "trial" && subscription?.is_trial;
  };

  const getTrialDaysLeft = () => {
    if (!subscription?.trial_ends_at) return 0;
    const trialEnds = new Date(subscription.trial_ends_at);
    const now = new Date();
    const diff = Math.ceil((trialEnds - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const value = {
    user,
    organization,
    subscription,
    loading,
    token,
    login,
    register,
    logout,
    refreshUserData,
    isPremium,
    hasSubscription,
    isTrialActive,
    getTrialDaysLeft
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
